import Resource from 'components/Resources/Resource';
import Carousel from 'components/shared/Carousel/Carousel';
import CarouselContent from 'components/shared/Carousel/CarouselContent';
import CarouselPagination from 'components/shared/Carousel/CarouselPagination';
import useCarousel from 'hooks/useCarousel';
import { useAutoplayPlugin } from 'hooks/useAutoplayPlugin';
import Link from 'components/shared/Link/Link';
import { NavProps } from 'components/MainNav/types';
import { HEADER_NAV_ID_PREFIX } from 'components/MainNav/utils';
import { OverflowVisibleSettings } from 'types/constants';
import AutoplayToggleButton from 'components/shared/Carousel/AutoplayToggleButton';
import MediumArticle, { MediumArticleDisplayVariant } from 'components/ArticleGrid/MediumArticle';
import { resourceToArticleShim } from 'components/ArticleGrid/utils';
import { Colorways } from 'types/constants';
import { cn } from 'utils/styles';

export const NavCarouselCardVariant = {
  Resource: 'resource',
  MediumArticle: 'mediumArticle',
} as const;
export type NavCarouselCardVariant = (typeof NavCarouselCardVariant)[keyof typeof NavCarouselCardVariant];

export type NavCarouselProps = Omit<NavProps, 'topBarLink' | 'cta' | 'navContent'> & {
  /** Optional id prefix for link and pagination. Use when rendering outside main nav to avoid duplicate ids. */
  idPrefix?: string;
  /** Card variant: Resource (default) or MediumArticle (square medium cards, e.g. blog subnav). */
  cardVariant?: NavCarouselCardVariant;
};

const NavCarousel = ({
  resourcesLabel,
  resourcesCollection,
  resourcesUrl,
  resourcesInternalPage,
  idPrefix = HEADER_NAV_ID_PREFIX,
  cardVariant = NavCarouselCardVariant.Resource,
}: NavCarouselProps) => {
  const { current, setApi, scrollTo } = useCarousel();
  const resources = resourcesCollection.items;
  const useMediumCard = cardVariant === NavCarouselCardVariant.MediumArticle;
  const articles = useMediumCard ? resources.map(r => resourceToArticleShim(r)) : [];

  const autoPlayOptions = {
    delay: 4000,
    stopOnInteraction: true,
    stopOnMouseEnter: true,
    rootNode: (emblaRoot: HTMLElement) => emblaRoot.parentElement,
  };
  const autoplayPlugin = useAutoplayPlugin(autoPlayOptions);

  const containerMaxWidth = useMediumCard ? 'md:max-w-[280px]' : 'max-w-[315px]';
  const slideGap = useMediumCard ? 'gap-4' : 'gap-5';

  return (
    <div className={containerMaxWidth}>
      {resourcesUrl ? (
        <Link
          id={`${idPrefix}_itg_link`}
          internalPage={resourcesInternalPage}
          href={resourcesUrl}
          className="font-sans font-semibold pb-4 text-label-saans hover:text-fern focus:text-fern">
          {resourcesLabel}
        </Link>
      ) : (
        <div className="text-content-secondary font-sans font-semibold pb-4 text-label-saans">{resourcesLabel}</div>
      )}

      <Carousel
        aria-label="Nav carousel"
        aria-roledescription="Nav carousel"
        className="flex flex-col gap-4"
        setApi={setApi}
        plugins={autoplayPlugin ? [autoplayPlugin] : []}
        opts={{ align: 'start', slidesToScroll: 1, containScroll: false, loop: !useMediumCard }}>
        <CarouselContent
          overflowVisible={OverflowVisibleSettings.None}
          className="w-full"
          contentClassName="pl-2 -ml-2">
          <div className={cn('flex', slideGap)}>
            {useMediumCard
              ? articles.map((article, index) => (
                  <div
                    key={article.sys?.id || index}
                    className="flex-none w-full md:max-w-[280px] aspect-square min-w-0 shrink-0 overflow-hidden">
                    <MediumArticle
                      {...article}
                      displayVariant={MediumArticleDisplayVariant.Card}
                      colorway={Colorways.Inverse}
                      className="h-full w-full flex flex-col"
                    />
                  </div>
                ))
              : resources.map((resource, index) => (
                  <Resource
                    className="flex-none md:flex-none w-full pb-0.5 mx-3 max-h-min"
                    contentClassName="flex-row [&>div:first-child]:w-[110px] [&>div:first-child]:h-[110px]"
                    {...resource}
                    key={index}
                    index={index}
                    idPrefix={`${idPrefix}_slider`}
                  />
                ))}
          </div>
        </CarouselContent>
        <div className="flex justify-start items-center gap-x-4 mb-6">
          <CarouselPagination
            total={resources.length}
            current={current}
            onSelect={scrollTo}
            isTimed
            timeDuration={autoPlayOptions.delay / 1000}
            idPrefix={idPrefix}
          />
          <AutoplayToggleButton />
        </div>
      </Carousel>
    </div>
  );
};

export default NavCarousel;
